import React, { useEffect, useState, useContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Input from "shared/components/FormElements/Input";
import Select from "shared/components/FormElements/Select";
import DatePicker from "shared/components/FormElements/DatePicker";
import Button from "shared/components/FormElements/Button";
import NotFound from "shared/components/UIElements/NotFound";
import FormFooter from "shared/components/FormElements/FormFooter";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { PERMISSION_TYPES } from "@routebot/constants";

import { parseComplexDate } from "shared/util/util";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";

// Benzersiz ID üreteci
let usedStockIdCounter = 0;
const generateUsedStockId = () => {
  usedStockIdCounter += 1;
  return `usedStock_${Date.now()}_${usedStockIdCounter}`;
};

const InventoryMaintenanceItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const { t } = useTranslation();

  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { maintenances, vehicleOptions, vehicles, thirdParties, stocks, RetrieveStocks } = useContext(StoreContext);
  const auth = useContext(AuthContext);

  // Bakıma eklenmek üzere olan YENİ stoklar.
  // Hem yeni bakım oluştururken hem mevcut bakıma stok eklerken kullanılır.
  // Mevcut bakımdaki kayıtlı stoklar bu state'e YÜKLENMEZ; onlar
  // loadedItem.usedStocks üzerinden readonly gösterilir ve değiştirilemez.
  const [usedStocks, setUsedStocks] = useState([]);

  // Stock options for dropdown
  const stockOptions = useMemo(
    () =>
      (stocks || [])
        .filter((stock) => stock.isActive && stock.currentQuantity > 0)
        .map((stock) => ({
          value: stock.id,
          label: t("inventoryMaintenance.usedStocks.optionLabel", {
            stockNumber: stock.stockNumber,
            name: stock.name,
            quantity: stock.currentQuantity,
            unit: stock.unit || t("inventoryMaintenance.usedStocks.defaultUnit"),
          }),
          stockName: stock.name,
          stockNumber: stock.stockNumber,
          currentQuantity: stock.currentQuantity,
        })),
    [stocks]
  );

  // Eklenmek üzere olan tüm stoklar geçerli mi (hem yeni bakım hem mevcut bakım için)
  const areUsedStocksValid = useMemo(() => {
    if (usedStocks.length === 0) return true;
    return usedStocks.every((item) => item.stock && item.quantity >= 1);
  }, [usedStocks]);

  // Used stocks handlers
  const handleAddUsedStock = () => {
    setUsedStocks([...usedStocks, { _id: generateUsedStockId(), stock: "", quantity: 1 }]);
  };

  const handleUpdateUsedStock = (id, field, value) => {
    setUsedStocks((prev) =>
      prev.map((item) => (item._id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveUsedStock = (id) => {
    setUsedStocks((prev) => prev.filter((item) => item._id !== id));
  };

  const itemType = "maintenance";
  const itemTextType = "maintenance";
  const endPointUpdate = "vehicle-maintenances";
  const endPointDelete = "vehicle-maintenances";
  const endPointCreate = "vehicle-maintenances";

  const [formState, inputHandler] = useForm(
    {
      vehicle: { value: "", isValid: false },
      maintenanceDate: { value: "", isValid: true },
      maintenanceOdometer: { value: "", isValid: true },
      nextMaintenanceDate: { value: "", isValid: true },
      nextMaintenanceOdometer: { value: "", isValid: true },
      serviceCenter: { value: "", isValid: true },
      serviceCenterPhone: { value: "", isValid: true },
      servicePartner: { value: "", isValid: true },
      maintenanceDescription: { value: "", isValid: true },
      cost: { value: "", isValid: true },
      notes: { value: "", isValid: true },
      maintenanceType: { value: "", isValid: true },
      maintenanceStatus: { value: "devam_ediyor", isValid: true },
      fatura: { value: "", isValid: true },
      fis: { value: "", isValid: true },
    },
    false
  );

  const servicePartnerOptions = useMemo(
    () =>
      (thirdParties || [])
        .filter((partner) => partner.type === "service")
        .map((partner) => ({
          value: partner.id,
          label: partner.name,
        })),
    [thirdParties]
  );

  const [initialServicePartner, setInitialServicePartner] = useState(null);

  const servicePartnerInputHandler = useCallback(
    (id, value, isValid) => {
      inputHandler(id, value, isValid);

      const partner = thirdParties?.find((item) => item.id === value);
      if (partner) {
        inputHandler("serviceCenter", partner.name || "", true);
        inputHandler("serviceCenterPhone", partner.phone || "", true);
      }
    },
    [inputHandler, thirdParties]
  );

  const numberToString = (value) => (value || value === 0 ? value.toString() : "");

  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentMaintenance = maintenances.find((maintenance) => maintenance.id === itemId);
      setLoadedItem(currentMaintenance);

      if (currentMaintenance) {
        const partnerId = currentMaintenance.servicePartner;
        let match = null;

        if (partnerId) {
          match = thirdParties?.find((partner) => partner.id === partnerId);
        } else if (currentMaintenance?.serviceCenter) {
          match = thirdParties?.find(
            (partner) => partner.type === "service" && partner.name === currentMaintenance.serviceCenter
          );
        }

        if (match) {
          setInitialServicePartner({ value: match.id, label: match.name });
          inputHandler("servicePartner", match.id, true);
        } else {
          setInitialServicePartner(null);
        }
      } else {
        setInitialServicePartner(null);
      }
    } catch (err) {
      console.log(err);
    }
    setIsItemLoaded(true);
  }, [maintenances, itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  // usedStocks (eklenmek üzere olan yeni stoklar) sadece itemId değiştiğinde
  // (örn. kullanıcı farklı bir bakıma geçtiğinde) sıfırlanır. Bu sayede arka
  // planda maintenances dizisi yenilense bile (örn. başka bir sekmedeki kayıt
  // veya RetrieveMaintenances çağrısı) kullanıcının yazmakta olduğu yeni stok
  // satırları kaybolmaz. Başarılı save sonrası temizlik ise itemUpdateSubmitHandler
  // içinde explicit setUsedStocks([]) ile yapılır.
  useEffect(() => {
    setUsedStocks([]);
  }, [itemId]);

  useEffect(() => {
    if (!loadedItem) {
      if (isNewItem) {
        inputHandler("serviceCenter", "", true);
        inputHandler("serviceCenterPhone", "", true);
      }
      return;
    }

    inputHandler("serviceCenter", loadedItem.serviceCenter || "", true);
    inputHandler("serviceCenterPhone", loadedItem.serviceCenterPhone || "", true);
  }, [loadedItem, isNewItem, inputHandler]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    try {
      const infoToBeSent = {
        vehicle: formState.inputs.vehicle.value,
        maintenanceDate: formState.inputs.maintenanceDate.value || null,
        maintenanceOdometer: formState.inputs.maintenanceOdometer.value
          ? parseInt(formState.inputs.maintenanceOdometer.value, 10)
          : null,
        nextMaintenanceDate: formState.inputs.nextMaintenanceDate.value || null,
        nextMaintenanceOdometer: formState.inputs.nextMaintenanceOdometer.value
          ? parseInt(formState.inputs.nextMaintenanceOdometer.value, 10)
          : null,
        serviceCenter: formState.inputs.serviceCenter.value,
        serviceCenterPhone: formState.inputs.serviceCenterPhone.value,
        servicePartner: formState.inputs.servicePartner.value || null,
        maintenanceDescription: formState.inputs.maintenanceDescription.value,
        cost: formState.inputs.cost.value ? parseFloat(formState.inputs.cost.value) : null,
        notes: formState.inputs.notes.value,
        maintenanceType: formState.inputs.maintenanceType.value || null,
        maintenanceStatus: formState.inputs.maintenanceStatus.value || "devam_ediyor",
        fatura: formState.inputs.fatura.value || null,
        fis: formState.inputs.fis.value || null,
      };

      // Hem yeni bakım oluştururken hem mevcut bakıma stok eklerken aynı payload kullanılır.
      // PATCH'te usedStocks ADDITIVE'tir; mevcut stokları değiştirmez, sadece yenilerini ekler.
      const validUsedStocks = usedStocks.filter((us) => us.stock && us.quantity > 0);
      if (isNewItem || validUsedStocks.length > 0) {
        infoToBeSent.usedStocks = validUsedStocks;
      }

      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
        : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";

      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent), {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      });

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
          // "Yeni bir bakım oluştur" akışına dönüldüğünde form temiz başlasın.
          // Daha önce bu temizliği fetchItem yapıyordu; artık fetchItem
          // maintenances değişikliklerinde usedStocks'a dokunmuyor, bu yüzden
          // explicit temizliyoruz.
          setUsedStocks([]);
          if (validUsedStocks.length > 0 && typeof RetrieveStocks === "function") {
            RetrieveStocks();
          }
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
          // Yeni stok eklendiyse: form state'ini temizle ve envanteri yenile (currentQuantity güncel kalsın).
          if (validUsedStocks.length > 0) {
            setUsedStocks([]);
            if (typeof RetrieveStocks === "function") {
              RetrieveStocks();
            }
          }
        }
      }
    } catch (err) {
      console.log(err);
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code));
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemTextType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemTextType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemTextType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container">
          <div className="horizontal-flex">
            <Select
              id="vehicle"
              label={`${t("inventoryMaintenance.fields.vehicle")} *`}
              options={vehicleOptions || []}
              validators={[VALIDATOR_REQUIRE()]}
              onInput={inputHandler}
              initialValue={vehicleOptions?.find((vehicle) => vehicle.value === loadedItem?.vehicle) || null}
              initialValid={!isNewItem}
            />
          </div>

          {(() => {
            const selectedVehicleId = formState.inputs.vehicle.value || loadedItem?.vehicle;
            if (!selectedVehicleId || !vehicles?.length) {
              return null;
            }
            const vehicleRecord = vehicles.find((vehicle) => vehicle.id === selectedVehicleId);
            const folderId = vehicleRecord?.folder?.id;
            if (!folderId) {
              return null;
            }
            return (
              <div style={{ marginBottom: "1rem" }}>
                <div className="horizontal-flex" style={{ alignItems: "center", justifyContent: "flex-start" }}>
                  <div>
                    <Button type="button" href={`https://drive.google.com/drive/folders/${folderId}`}>
                      {t("inventoryCommon.goToFiles")}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="horizontal-flex" style={{ justifyContent: "space-between" }}>
            <Select
              id="maintenanceStatus"
              label={t("inventoryMaintenance.fields.maintenanceStatus")}
              options={[
                { value: "devam_ediyor", label: t("inventoryMaintenance.maintenanceStatus.inProgress") },
                { value: "bitti", label: t("inventoryMaintenance.maintenanceStatus.finished") },
              ]}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={
                isNewItem
                  ? {
                      value: "devam_ediyor",
                      label: t("inventoryMaintenance.maintenanceStatus.inProgress"),
                    }
                  : loadedItem?.maintenanceStatus === "devam_ediyor"
                  ? {
                      value: "devam_ediyor",
                      label: t("inventoryMaintenance.maintenanceStatus.inProgress"),
                    }
                  : {
                      value: "bitti",
                      label: t("inventoryMaintenance.maintenanceStatus.finished"),
                    }
              }
              initialValid={true}
            />
            <Select
              id="maintenanceType"
              label={t("inventoryMaintenance.fields.maintenanceType")}
              options={[
                { value: "bakım", label: t("inventoryMaintenance.maintenanceTypes.maintenance") },
                { value: "onarım", label: t("inventoryMaintenance.maintenanceTypes.repair") },
              ]}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={
                isNewItem
                  ? {
                      value: "bakım",
                      label: t("inventoryMaintenance.maintenanceTypes.maintenance"),
                    }
                  : loadedItem?.maintenanceType
                  ? {
                      value: loadedItem.maintenanceType,
                      label: t(
                        `inventoryMaintenance.maintenanceTypes.${
                          loadedItem.maintenanceType === "bakım" ? "maintenance" : "repair"
                        }`
                      ),
                    }
                  : null
              }
              initialValid={true}
            />

            <DatePicker
              id="maintenanceDate"
              label={t("inventoryMaintenance.fields.maintenanceDate")}
              onInput={inputHandler}
              initialValue={isNewItem ? parseComplexDate(new Date()) : loadedItem?.maintenanceDate || ""}
              initialValid={true}
            />

            <Input
              id="maintenanceOdometer"
              element="input"
              type="number"
              label={t("inventoryMaintenance.fields.maintenanceOdometer")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={numberToString(loadedItem?.maintenanceOdometer)}
              initialValid={true}
            />

            <Input
              id="cost"
              element="input"
              type="number"
              label={t("inventoryMaintenance.fields.cost")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={numberToString(loadedItem?.cost)}
              initialValid={true}
            />
          </div>

          {/* <div className="horizontal-flex" style={{ gap: "1rem" }}>
            <div style={{ flex: 1 }}>
              <label className="read-only-label">{t("inventoryMaintenance.fields.serviceCenter")}</label>
              <div className="read-only-value">{formState.inputs.serviceCenter.value || "-"}</div>
            </div>
            <div style={{ flex: 1 }}>
              <label className="read-only-label">{t("inventoryMaintenance.fields.serviceCenterPhone")}</label>
              <div className="read-only-value">{formState.inputs.serviceCenterPhone.value || "-"}</div>
            </div>
          </div> */}

          <div className="horizontal-flex">
            <Select
              id="servicePartner"
              label={t("inventoryMaintenance.fields.servicePartner")}
              options={servicePartnerOptions}
              validators={[VALIDATOR_NONE()]}
              onInput={servicePartnerInputHandler}
              initialValue={initialServicePartner}
              initialValid={true}
            />
            <DatePicker
              id="nextMaintenanceDate"
              label={t("inventoryMaintenance.fields.nextMaintenanceDate")}
              onInput={inputHandler}
              initialValue={loadedItem?.nextMaintenanceDate || ""}
              initialValid={true}
            />

            <Input
              id="nextMaintenanceOdometer"
              element="input"
              type="number"
              label={t("inventoryMaintenance.fields.nextMaintenanceOdometer")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={numberToString(loadedItem?.nextMaintenanceOdometer)}
              initialValid={true}
            />
          </div>

          <div className="horizontal-flex">
            <Input
              id="maintenanceDescription"
              element="textarea"
              label={t("inventoryMaintenance.fields.maintenanceDescription")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              rows={3}
              initialValue={loadedItem?.maintenanceDescription || ""}
              initialValid={true}
            />
          </div>

          <div className="horizontal-flex">
            <Input
              id="fatura"
              element="input"
              type="text"
              label={t("inventoryMaintenance.fields.fatura")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.fatura || ""}
              initialValid={true}
            />

            <Input
              id="fis"
              element="input"
              type="text"
              label={t("inventoryMaintenance.fields.fis")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.fis || ""}
              initialValid={true}
            />
          </div>

          <Input
            id="notes"
            element="textarea"
            label={t("inventoryMaintenance.fields.notes")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            rows={5}
            initialValue={loadedItem?.notes || ""}
            initialValid={true}
          />

          {/* Kullanılan Stoklar Bölümü */}
          <div style={{ marginTop: "20px", marginBottom: "20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <h4 style={{ margin: 0 }}>{t("inventoryMaintenance.usedStocks.sectionTitle")}</h4>
              <Button type="button" onClick={handleAddUsedStock}>
                {t("inventoryMaintenance.usedStocks.addStock")}
              </Button>
            </div>

            {/* Mevcut bakımda kayıtlı stoklar (readonly) */}
            {!isNewItem && loadedItem?.usedStocks && loadedItem.usedStocks.length > 0 && (
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "15px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  marginBottom: "15px",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #ddd" }}>
                      <th style={{ textAlign: "left", padding: "8px" }}>{t("inventoryMaintenance.usedStocks.stockNumber")}</th>
                      <th style={{ textAlign: "left", padding: "8px" }}>{t("inventoryMaintenance.usedStocks.stockName")}</th>
                      <th style={{ textAlign: "right", padding: "8px" }}>{t("inventoryMaintenance.usedStocks.quantity")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadedItem.usedStocks.map((us, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{ padding: "8px" }}>{us.stockNumber || "-"}</td>
                        <td style={{ padding: "8px" }}>{us.stockName || "-"}</td>
                        <td style={{ textAlign: "right", padding: "8px" }}>{us.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ color: "#666", fontSize: "12px", marginTop: "10px", marginBottom: 0 }}>
                  {t("inventoryMaintenance.usedStocks.readonlyNote")}
                </p>
              </div>
            )}

            {/* Boş durum mesajı - sadece hiçbir stok yokken ve eklenmemişken */}
            {usedStocks.length === 0 &&
              (isNewItem || !loadedItem?.usedStocks || loadedItem.usedStocks.length === 0) && (
                <p style={{ color: "#888", fontStyle: "italic" }}>
                  {isNewItem
                    ? t("inventoryMaintenance.usedStocks.emptyNew")
                    : t("inventoryMaintenance.usedStocks.emptyExisting")}
                </p>
              )}

            {/* Eklenmek üzere olan yeni stoklar (hem yeni bakım hem mevcut bakım için) */}
            {usedStocks.map((stockItem, index) => {
              const selectedStock = stockOptions.find((s) => s.value === stockItem.stock);
              const isRowInvalid = !stockItem.stock || !stockItem.quantity || stockItem.quantity < 1;
              return (
                <div
                  key={stockItem._id}
                  className="horizontal-flex"
                  style={{
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "10px",
                    padding: "10px",
                    borderRadius: "4px",
                    border: isRowInvalid ? "1px solid #f44336" : "1px solid #e0e0e0",
                    backgroundColor: isRowInvalid ? "#fff3f3" : "transparent",
                  }}
                >
                  <div style={{ flex: 2 }}>
                    <Select
                      id={`usedStock-${stockItem._id}`}
                      label={index === 0 ? t("inventoryMaintenance.usedStocks.stock") : ""}
                      options={stockOptions}
                      validators={[VALIDATOR_NONE()]}
                      onInput={(id, value) => handleUpdateUsedStock(stockItem._id, "stock", value)}
                      initialValue={selectedStock || null}
                      initialValid={true}
                      noMargin
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Input
                      id={`usedStockQty-${stockItem._id}`}
                      element="input"
                      type="number"
                      label={index === 0 ? t("inventoryMaintenance.usedStocks.quantity") : ""}
                      validators={[VALIDATOR_NONE()]}
                      onInput={(id, value) => handleUpdateUsedStock(stockItem._id, "quantity", parseFloat(value) || 0)}
                      initialValue={stockItem.quantity?.toString() || ""}
                      initialValid={true}
                      noMargin
                    />
                  </div>
                  <div style={{ paddingTop: index === 0 ? "25px" : "0" }}>
                    <Button type="button" danger onClick={() => handleRemoveUsedStock(stockItem._id)}>
                      {t("inventoryMaintenance.usedStocks.remove")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY] && (
            <FormFooter
              isNewItem={isNewItem}
              onUpdate={itemUpdateSubmitHandler}
              onDelete={() => setShowConfirmModal(true)}
              isUpdating={isLoading}
              disabled={!formState.isValid || isLoading || !formState.inputs.vehicle.value || !areUsedStocksValid}
            />
          )}
        </form>
      )}
    </React.Fragment>
  );
};

export default InventoryMaintenanceItem;
